import { returnExecutionRequestColDefsOP } from "@/components/widgets/tradeActionSummary/helper/colDefs/executionRequestColDefsOP";
import { returnExecutionRequestColDefsRP } from "@/components/widgets/tradeActionSummary/helper/colDefs/executionRequestColDefsRP";
import {
  statusAtAffirmedRange,
  statusAtExecutedAndAwardedRange,
  statusAtExecutedRange,
  statusFormExecutedToAffirmedRange,
  statusFromAwarded,
  isEditPanelAllowedAtStatus,
  shouldShowPercentQuoted,
  tradeHasIceberg,
} from "@/components/widgets/tradeActionSummary/helper/colDefs/colDefsShared";
import { onNodeCollapseChangeAllSettingsToFalse } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import { hasCoverValue } from "@/components/widgets/tradeActionSummary/helper/cellRenderers/CoverCellRenderer/coverFormatter";
import {
  IColDefsObject,
  returnColDefsForAllocations,
  returnColDefsForEditingPanel,
  returnColDefsForLegs,
  returnColDefsForTradeAtNew,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryMainTableColDefs";
// import { ExecutionRequestStatus } from "@/lib/actionSummary/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import {
  ExecutionRequestQuoteLeg,
  IFlattenedLegRow,
  IOrderFill,
} from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { delayViaPromise } from "@/lib/utilities";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import { getBlocksData } from "@/store/tradeBlockDataStoreACTIONS";
import tradeDataStore from "@/store/tradeDataStore";
import { initializeFillsForWorkingOrders } from "@/store/tradeDataStoreACTIONS";
import requestInformationStore from "@/store/requestInformationStore";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import { User } from "@auth0/auth0-react";
import { IRowNode } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Dispatch, RefObject, SetStateAction } from "react";
import { handleTradeNotification } from "@/lib/notificationHelper";
import { fillsReplicatedColIds } from "./helper/actionSummaryFillsColDefs";

export interface IActionSummaryTable {
  cryptoID?: string | undefined;
  getAccessTokenSilently: Function;
  user: User | undefined;
  dataForMainTable: any;
  isMiniTable?: boolean;
  tradeData?: ExecutionStrategy[];
}

export interface IdefaultExpandedRowContext {
  esid: string;
  allocationsShowing: boolean;
  legsShowing: boolean;
  editPanelShowing: boolean;
}

export const defaultExpandedRowContext: IdefaultExpandedRowContext = {
  esid: "",
  allocationsShowing: false,
  editPanelShowing: false,
  legsShowing: false,
};

export interface IFindChangedDataOutput {
  type: string;
  id: string;
  changes: any;
  newItem?: undefined;
  structureType: StructureTypeCode;
  strategyType: StrategyType;
  role: string;
  owner?: string;
  unwind: boolean;
  containsRecoupon?: boolean;
  containsNPV?: boolean;
  currentStatus?: ExecutionStrategyStatus;
  tradeType?: TradeType;
  latestActivity?: string;
  cover?: any;
  coverSpread?: any;
  competition?: any;
}

// Determines if the ER level table shows the legs.
export interface ITradeDetails {
  isOp: boolean;
  isOutright: boolean;
  isRFQ: boolean;
  isNotSentAndClosed: boolean;
  isViewingAllocaitons: boolean;
  isTradeStatusNew: boolean;

  isEditingPanel: boolean;
  isViewingLegs: boolean; //Used at status new. Could it be used on all status?
  isUnwind: boolean;
  isCancelledOPWithNoCP: boolean;
}

export function masterDetialAtERLevel(tradeDetails: ITradeDetails): boolean {
  const {
    isViewingAllocaitons,
    isOp,
    isOutright,
    isRFQ,
    isNotSentAndClosed,
    isViewingLegs,
    isEditingPanel,
    isTradeStatusNew,
  } = tradeDetails;

  // Don't show nested leg table when viewing edit panel
  if (isEditingPanel) {
    return false;
  }
  // When the trade is at new there is no master / nested table - returning true will result in a loss of focus error.
  if (isTradeStatusNew) {
    return false;
  }
  // Don't show nested leg table for Cancelled or Dealt away trades
  if (isNotSentAndClosed) {
    return false;
  }
  if (isViewingAllocaitons || isViewingLegs) {
    return true;
  } else if (isOp) {
    if (!isOutright || !isRFQ) {
      // || isUnwind - Is unwind removed. Will be handled like a normal outright.
      return true;
    }
  } else if (!isOp) {
    if (!isOutright || !isRFQ) {
      // || isUnwind - Is unwind removed. Will be handled like a normal outright.
      // currently the same as OP.
      return true;
    }
  }
  return false;
}

// Variable for future settings: controls whether ER tables default to expanded.
// Can be wired to user settings in the future.
export const defaultERTableExpanded = true;

// Determines if the leg table is set to expanded by default.
export function setLegTableToExpanded(tradeDetails: ITradeDetails): number {
  const {
    isViewingAllocaitons,
    isOp,
    isOutright,
    isRFQ,
    isViewingLegs,
    isUnwind,
  } = tradeDetails;
  // Return of 1 will have the table set to expanded.
  if (isViewingAllocaitons || isViewingLegs) {
    return 0;
  } else if (isOp) {
    // Both Orders and RFQs with executionCounterparties open expanded by default.
    // Controlled by defaultERTableExpanded for future settings.
    return defaultERTableExpanded ? 1 : 0;
  } else {
    // RP conditions
    if (!isOutright || !isRFQ || isUnwind || !isRFQ) {
      return 1;
    } else {
      return 0;
    }
  }
}

// Could this be replaced by a store object that the websocket pushes too?
// HELPER FUNTION - used to provide a comparison obj to use for comparing old to new data and base logic for redrawing data.
export function findChangedDataForMainActionSummary<
  T extends Record<string, any>,
>(oldData: T[], newData: T[], key: string) {
  // Create a map of the old data for quick lookup
  if (oldData == undefined || oldData[0] == undefined) {
    return;
  }
  const oldDataMap = new Map(oldData.map((item) => [item[key], item]));

  // Iterate through the new data and compare against the old data
  const changes = newData.map((newItem) => {
    const oldItem = oldDataMap.get(newItem[key]);

    // If there's no corresponding item in the old data, it's a new row
    if (!oldItem) {
      return { type: "added", newItem };
    }

    // Compare the fields and find the changes
    const fieldChanges = Object.keys(newItem).reduce(
      (acc: Record<string, any>, field: string) => {
        if (newItem[field] !== oldItem[field]) {
          acc[field] = {
            oldValue: oldItem[field] === undefined ? null : oldItem[field], // Normalize undefined to null for better tracking
            newValue: newItem[field],
          };
        }
        return acc;
      },
      {},
    );

    // If there are no changes, skip this row
    if (Object.keys(fieldChanges).length === 0) {
      return null;
    }

    let containsRecoupon = false;
    let containsNPV = false;
    if (newItem.role == "OP") {
      if (
        newItem.executionRequestCounterParties &&
        newItem.executionRequestCounterParties.length > 0 &&
        newItem.executionRequestCounterParties[0].legs
      ) {
        newItem.executionRequestCounterParties[0].legs.forEach((leg: any) => {
          if (leg.unwind == "Recoupon") {
            containsRecoupon = true;
          }
          if (leg.unwind == "NPV") {
            containsNPV = true;
          }
        });
      }
    } else if (newItem.role == "RP") {
      if (newItem?.legs?.length > 0) {
        newItem.legs.forEach((leg: any) => {
          if (leg.unwind == "Recoupon") {
            containsRecoupon = true;
          } else if (leg.unwind == "NPV") {
            containsNPV = true;
          }
        });
      }
    }

    return {
      type: "updated",
      id: newItem[key],
      currentStatus: newItem.status,
      changes: fieldChanges,
      structureType: newItem.structureType,
      strategyType: newItem.strategyType,
      role: newItem.role,
      owner: newItem.owner,
      unwind: newItem.unwind,
      containsRecoupon: containsRecoupon,
      containsNPV: containsNPV,
      tradeType: newItem.type,
      latestActivity: newItem.latestActivity,
      cover: newItem.cover,
      coverSpread: newItem.coverSpread,
      competition: newItem.competition,
    };
  });

  // Find rows that are in the old data but not in the new data
  const removed = [...oldDataMap.keys()]
    .filter((id) => !newData.some((newItem) => newItem[key] === id))
    .map((id) => ({ type: "removed", id }));

  // Filter out nulls (unchanged rows) and concatenate with removed rows
  return [...changes.filter(Boolean), ...removed];
}

// Redrawing row Helpers
function handleRedrawingRowsWhenNoStatusChanges({
  trade,
  gridRef,
  expandedRowsContext,
}: {
  trade: IFindChangedDataOutput;
  gridRef: React.RefObject<AgGridReact<any>>;
  expandedRowsContext: IdefaultExpandedRowContext[];
}) {
  const esid = trade.id;
  if (
    trade.currentStatus != ExecutionStrategyStatus.New &&
    trade.changes.status == undefined
  ) {
    // ONLY redraw when the unwind value in the legs is different.
    if (trade.role == "RP") {
      if (
        trade.changes.legs &&
        trade.changes.legs.newValue &&
        trade.changes.legs.newValue.length > 0 &&
        trade.changes.legs.oldValue &&
        trade.changes.legs.oldValue.length > 0
      ) {
        if (
          trade.changes.legs.newValue.length ==
          trade.changes.legs.oldValue.length
        ) {
          for (let i = 0; i < trade.changes.legs.oldValue.length; i++) {
            const oldValue = trade.changes.legs.oldValue[i].unwind;
            const newValue = trade.changes.legs.newValue[i].unwind;

            if (
              (oldValue == "Recoupon" && newValue != "Recoupon") ||
              (newValue == "Recoupon" && oldValue != "Recoupon")
            ) {
              redrawRowsViaESID(trade.id, gridRef);
            }
          }
        }
      }
    } else if (trade.role == "OP") {
      // Don't redraw while the legs table or the Edit Panel is open.
      let redrawOP = true;
      if (expandedRowsContext) {
        const thisContext = expandedRowsContext.find((row) => row.esid == esid);
        if (thisContext) {
          if (thisContext.legsShowing || thisContext.editPanelShowing) {
            redrawOP = false;
          }
        }
      }
      // Both views host the recoupon checkboxes, and a redraw rebuilds the detail
      // row underneath the user - annoying mid-edit if they are ticking several
      // cells. The deferred redraw fires on collapse via expandedRowsContext.
      if (redrawOP) {
        if (
          trade.changes.executionRequestCounterParties &&
          trade.changes.executionRequestCounterParties.newValue &&
          trade.changes.executionRequestCounterParties.newValue.length > 0 &&
          trade.changes.executionRequestCounterParties.oldValue &&
          trade.changes.executionRequestCounterParties.oldValue.length > 0
        ) {
          if (
            trade.changes.executionRequestCounterParties.newValue.length ==
            trade.changes.executionRequestCounterParties.oldValue.length
          ) {
            if (
              trade.changes.executionRequestCounterParties.newValue[0].legs &&
              trade.changes.executionRequestCounterParties.oldValue[0].legs &&
              trade.changes.executionRequestCounterParties.newValue[0].legs
                .length > 0 &&
              trade.changes.executionRequestCounterParties.oldValue[0].legs
                .length > 0
            ) {
              for (
                let i = 0;
                i <
                trade.changes.executionRequestCounterParties.oldValue[0].legs
                  .length;
                i++
              ) {
                const oldValue =
                  trade.changes.executionRequestCounterParties.oldValue[0].legs[
                    i
                  ].unwind;
                const newValue =
                  trade.changes.executionRequestCounterParties.newValue[0].legs[
                    i
                  ].unwind;

                if (
                  (oldValue == "Recoupon" && newValue != "Recoupon") ||
                  (newValue == "Recoupon" && oldValue != "Recoupon")
                ) {
                  redrawRowsViaESID(trade.id, gridRef);
                }
              }
            }
          }
        }
      }
    }
  }
}

// This is the brain of the logic that will determine when to redraw detail rows.
// Redrawing rows will cause the detail rendering to occur - needs to be used every time it's expected that the structure of a table is required. Aim is to not over do this as it may cause flickering with the tables and will break the API link on the tables.
export function handleRedrawingRowsForActionSummaryTable({
  gridRef,
  trade,
  expandedRowsContext,
  setExpandedRowsContext,
  setExpandedRowsContextMostRecentlyUpdated,
  getAccessTokenSilently,
}: {
  gridRef: React.RefObject<AgGridReact<any>>;
  trade: IFindChangedDataOutput;
  expandedRowsContext: IdefaultExpandedRowContext[];
  setExpandedRowsContext: Dispatch<
    SetStateAction<IdefaultExpandedRowContext[]>
  >;
  setExpandedRowsContextMostRecentlyUpdated: React.Dispatch<
    React.SetStateAction<string>
  >;
  getAccessTokenSilently: Function;
}) {
  if (trade.changes) {
    const esid = trade.id;
    refreshCell(gridRef, esid, "ActionSummaryStatusCell"); // Forces the status to update on each data change.
    refreshCell(gridRef, esid, "ActionSummaryIndicativeMid");

    if (trade.changes.status) {
      // Play notification sound and log message for status changes
      handleTradeNotification({
        newStatus: trade.changes.status.newValue,
        oldStatus: trade.changes.status.oldValue,
        tradeId: trade.id,
        role: trade.role,
        owner: trade.owner,
        latestActivity: trade.latestActivity,
        showConsoleLog: true,
      });

      // LQC-1653 / LQC-2860: close an open Edit Panel once the new status no
      // longer offers it (Awarded, Working, Expired, Not Dealt ...). The context
      // change redraws the detail as the default view for that status.
      const editPanelOpenForTrade = expandedRowsContext.some(
        (ctx) => ctx.esid === esid && ctx.editPanelShowing,
      );
      if (
        editPanelOpenForTrade &&
        !isEditPanelAllowedAtStatus(
          trade.changes.status.newValue,
          trade.tradeType,
        )
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          expandedRowsContext,
          setExpandedRowsContext,
          esid,
          setExpandedRowsContextMostRecentlyUpdated,
        );
      }

      if (
        trade.changes.status.oldValue == ExecutionStrategyStatus.New || // old status of new means it has changed to not new.
        trade.changes.status.newValue == ExecutionStrategyStatus.New
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          // Set the trade settings to no longer be showing the CP list.
          expandedRowsContext,
          setExpandedRowsContext,
          trade.id,
          setExpandedRowsContextMostRecentlyUpdated,
        );
        // redrawRowsViaESID(trade.id, gridRef); // id is an esid
        refreshCell(gridRef, esid, "ActionSummaryBid");
        refreshCell(gridRef, esid, "ActionSummaryAsk");
        refreshCell(gridRef, esid, "ActionSummaryMid");
      } else if (
        trade.changes.status.newValue == ExecutionStrategyStatus.ReCreated
      ) {
        onNodeCollapseChangeAllSettingsToFalse(
          // Set the trade settings to no longer be showing the CP list.
          expandedRowsContext,
          setExpandedRowsContext,
          trade.id,
          setExpandedRowsContextMostRecentlyUpdated,
        );
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.changes.status.oldValue == ExecutionStrategyStatus.NotDealt || // old status of new means it has changed to not new.
        trade.changes.status.newValue == ExecutionStrategyStatus.Cancelled
      ) {
        getBlocksData({
          esid: esid,
          getAccessTokenSilently: getAccessTokenSilently,
        });
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.changes.status.newValue == ExecutionStrategyStatus.ReSent &&
        trade.changes.status.oldValue == ExecutionStrategyStatus.Recalled
      ) {
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        (trade.changes.status.newValue == ExecutionStrategyStatus.Working &&
          trade.changes.status.oldValue == ExecutionStrategyStatus.Received) ||
        trade.changes.status.oldValue == ExecutionStrategyStatus.ReSent ||
        trade.changes.status.oldValue == ExecutionStrategyStatus.Acknowledged
      ) {
        // Orders RP Specific
        // When status changes to "Working", initialize fills and redraw
        // so AG Grid re-evaluates masterDetail for the Order fills table
        ensureTradeFillsInitialized({ esid: trade.id });
        redrawRowsViaESID(trade.id, gridRef);
        // FOR the OP - Is this needed?
        // } else if (
        //   trade.changes.status.newValue == ExecutionStrategyStatus.Working &&
        //   trade.changes.status.oldValue == ExecutionStrategyStatus.Sent
        // ) {
        //   // Orders OP Specific - Should refresh and
        //   redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.tradeType === TradeType.Order &&
        (trade.changes.status.newValue === ExecutionStrategyStatus.Filled ||
          trade.changes.status.newValue === ExecutionStrategyStatus.Affirmed ||
          trade.changes.status.newValue ===
            ExecutionStrategyStatus.NoMoreFills ||
          trade.changes.status.newValue ===
            ExecutionStrategyStatus.ChangePending)
      ) {
        // Orders: When status changes to Filled, Affirmed, NoMoreFill, or ChangePending
        // Redraw to update fills table column visibility (dealt price) and button visibility
        // AG Grid caches masterDetail evaluation, so we need to redraw for it to re-evaluate
        redrawRowsViaESID(trade.id, gridRef);

        // For OP, expand blocks that have fills to show the nested fills table
        if (trade.role === "OP") {
          handleOPOrderFillsArriving(trade, gridRef);
        }
      } else if (
        trade.tradeType === TradeType.Order &&
        trade.changes.status.oldValue === ExecutionStrategyStatus.ChangePending
      ) {
        // Orders: When status changes FROM ChangePending (back to Filled/NoMoreFill or to Working)
        // Redraw to update fills table column visibility (dealt price)
        redrawRowsViaESID(trade.id, gridRef);
      } else if (trade.changes.unwind) {
        // In this situation - A trade is either getting SET / REMOVED NPV - Status NOT DEALT => ReSent.
        redrawRowsViaESID(trade.id, gridRef);
      } else if (
        trade.unwind &&
        trade.role === "RP" &&
        statusAtExecutedRange.includes(trade.changes.status.newValue)
      ) {
        // NPV unwind pack RP moving to Executed — regenerate column defs so quotedSize is re-evaluated.
        redrawRowsViaESID(trade.id, gridRef);
      }
    } else {
      // A CHANGE IS NOTICED BUT NO STATUS CHANGE.

      // Re-quote detection: status is Quoted and latestActivity is Quoted but no status change —
      // the RP updated their quote while the trade was already at Quoted.
      // Only fire if actionedAt is within the last 5 seconds to avoid stale re-diffs.
      if (
        trade.currentStatus === ExecutionStrategyStatus.Quoted &&
        trade.latestActivity === ExecutionStrategyStatus.Quoted &&
        trade.changes.actionedAt?.newValue
      ) {
        const actionedTime = new Date(
          trade.changes.actionedAt.newValue,
        ).getTime();
        const isRecent = Date.now() - actionedTime < 5000;

        if (isRecent) {
          handleTradeNotification({
            newStatus: trade.currentStatus as ExecutionStrategyStatus,
            tradeId: trade.id,
            role: trade.role,
            owner: trade.owner,
            latestActivity: "Re-Quoted",
            showConsoleLog: true,
          });
        }
      }

      // Currently this is getting used for when a block is flagged for / or removed as ReCoupon mid way through the trade.
      const unwind = trade.unwind;
      // Section for trades that have had their ReCoupon status changed through the trade life cycle and NO LONGER contain a recoupon block.
      if (!unwind) {
        handleRedrawingRowsWhenNoStatusChanges({
          trade,
          gridRef,
          expandedRowsContext,
        });
      }

      // Orders OP: Handle fills arriving while at Working status (No status change). handleOPOrderFillsArriving used above for when status DOES change.
      if (
        trade.role === "OP" &&
        trade.tradeType === TradeType.Order &&
        trade.currentStatus === ExecutionStrategyStatus.Working
      ) {
        handleOPOrderFillsArriving(trade, gridRef);
      }

      // Orders RP: Redraw detail grid when backend assigns new fillIds (no status change)
      // Only fires when fillIds change (backend update), not on frontend edits (no flicker)
      if (
        trade.role === "RP" &&
        trade.tradeType === TradeType.Order &&
        trade.currentStatus === ExecutionStrategyStatus.Working
      ) {
        handleRPOrderFillsUpdated(trade, gridRef);
      }
    }
    // END -  Can be moeved to a helper?? // Currently this is getting used for when a block is flagged for / or removed as ReCoupon mid way through the trade.

    // ONLY manange the API for Col visiblity if there is a status change.
    if (trade.changes.status != undefined) {
      handleShowOrHideCols(trade, gridRef);

      // const newStatus = trade.changes.status.newValue;
      // const isMovingToAwardedOrExecuted =
        // newStatus === ExecutionStrategyStatus.Awarded ||
      //   statusAtExecutedRange.includes(newStatus);
      // if (isMovingToAwardedOrExecuted) {
        
        if (trade.role === "RP") {
          syncERDetailRowData(esid, gridRef);
          refreshCellInLegLevel(esid, gridRef, ["quotedSize"], true);
          refreshColInERLevel(esid, gridRef, ["quotedSizeOutright", "quotedSize"], true);

        }
      // }
    }

    if (trade.changes.quotedSize) {
      const field = "quotedSize";
      flashCellInLegLevel(esid, gridRef, field);
    } else if (trade.changes.executionRequestCounterParties) {
      // redrawRowsViaESID(trade.id, gridRef);
      const field = "dealtStrikeOutright";
      flashCellsInERLevel(esid, gridRef, field);
    }

    // LQC-275: re-evaluate the OP % Quoted columns when leg data changes.
    // findChangedDataForMainActionSummary uses reference equality on nested
    // objects, so a leg's quotedSize change surfaces here as an
    // `executionRequestCounterParties` change rather than a top-level `quotedSize`.
    if (
      trade.role === "OP" &&
      (trade.changes.executionRequestCounterParties || trade.changes.quotedSize)
    ) {
      const liveTrade = tradeDataStore
        .getState()
        .tradeData.find((t) => t.esid === esid);
      if (shouldShowPercentQuoted(trade.tradeType, liveTrade)) {
        showOnLeg(esid, gridRef, ["percentQuoted"]);
        if (trade.strategyType == StrategyType.Outright) {
          showOnER(esid, gridRef, ["percentQuotedOutright"]);
        }
      } else {
        hideOnLeg(esid, gridRef, ["percentQuoted"]);
        if (trade.strategyType == StrategyType.Outright) {
          hideOnER(esid, gridRef, ["percentQuotedOutright"]);
        }
      }
    }

    // LQC-922: re-evaluate the OP Iceberg ER column when CP data changes.
    // releaseSize lives on the CP, so changes arrive via
    // `executionRequestCounterParties`.
    if (trade.role === "OP" && trade.changes.executionRequestCounterParties) {
      const liveTrade = tradeDataStore
        .getState()
        .tradeData.find((t) => t.esid === esid);
      const isOutrightRFQ =
        liveTrade?.type === TradeType.RFQ &&
        liveTrade?.strategyType === StrategyType.Outright;
      const icebergCol = isOutrightRFQ
        ? "percentIcebergOutright"
        : "percentIceberg";
      if (tradeHasIceberg(liveTrade)) {
        showOnER(esid, gridRef, [icebergCol]);
      } else {
        hideOnER(esid, gridRef, [icebergCol]);
      }
    }

    // LQC-1632: re-sync the RP cover/coverSpread/competition ("Comp") columns on
    // every non-status redraw so an ADVISE COMP edit shows/hides live. Not gated on
    // the per-field diff — a cleared value may not surface there. The function is
    // RP-gated and reads live values. Status changes go via handleShowOrHideCols.
    if (trade.changes.status == undefined) {
      handleCoverColumnVisibility(trade, gridRef);
    }
  }
}
interface gridNodeWithDetail extends IRowNode {
  detialNode: any;
}

export function redrawRowsViaESID(
  esid: string,
  gridRef: React.RefObject<AgGridReact<any>>,
) {
  if (gridRef.current && gridRef.current.api) {
    const gridNode: gridNodeWithDetail | any = // this gridNode will always have a detail node on it.
      gridRef.current.api.getRowNode(esid);
    if (gridNode != undefined && gridNode.detailNode) {
      gridRef.current.api.redrawRows({ rowNodes: [gridNode.detailNode] }); // Correct usage
    }
  }
}
// Export for testing purposes
export async function showOnER(
  esid: string,
  gridRef: any,
  fields: string[],
  forceRefresh: boolean = false,
  // Needed when Affirming and chang
) {
  const alreadyShown = isAlreadyVisibleColInERLevel(esid, gridRef, fields);
  if (alreadyShown && !forceRefresh) {
    // no need to refresh
    return;
  }
  controlVisibleColInERLevel(esid, gridRef, fields, true); // SHOW at ER
  await delayViaPromise(30);
  refreshColInERLevel(esid, gridRef, fields);
}

// Export for testing purposes
export async function hideOnER(esid: string, gridRef: any, fields: string[]) {
  const alreadyShown = isAlreadyVisibleColInERLevel(esid, gridRef, fields);
  if (!alreadyShown) {
    // no need to refresh
    return;
  }
  controlVisibleColInERLevel(esid, gridRef, fields, false); // SHOW at ER
  await delayViaPromise(30);
  refreshColInERLevel(esid, gridRef, fields);
}

// Export for testing purposes
export async function showOnLeg(esid: string, gridRef: any, fields: string[]) {
  const alreadyShown = isAlreadyVisibleColInLegLevel(esid, gridRef, fields);
  if (alreadyShown) {
    // no need to refresh
    return;
  }
  controlVisibleColInLegLevel(esid, gridRef, fields, true); // SHOW at Leg
  await delayViaPromise(30);
  refreshCellInLegLevel(esid, gridRef, fields);
}

// Export for testing purposes
export async function hideOnLeg(
  esid: string,
  gridRef: any,
  fields: string[],
  forceRefresh: boolean = false,
) {
  const alreadyShown = isAlreadyVisibleColInLegLevel(esid, gridRef, fields);
  if (!alreadyShown && !forceRefresh) {
    // no need to refresh --
    return;
  }
  controlVisibleColInLegLevel(esid, gridRef, fields, false); // HIDE at Leg
  await delayViaPromise(30);
  refreshCellInLegLevel(esid, gridRef, fields);
}

// Interface for column visibility actions
export interface ColumnVisibilityActions {
  showOnER: string[];
  hideOnER: string[];
  showOnLeg: string[];
  hideOnLeg: string[];
}

// Export for testing purposes
export function columnVisabilityNPVandRecoupon(
  trade: any,
  gridRef: any,
): ColumnVisibilityActions {
  // NOT ADDRESSING OUTRIGHTS HERE
  const role = trade.role;
  const esid = trade.id;
  const unwind = trade.unwind;
  const containsRecoupon = trade.containsRecoupon;
  const containsNPV = trade.containsNPV;

  // Initialize the actions object to track what columns should be shown/hidden
  const actions: ColumnVisibilityActions = {
    showOnER: [],
    hideOnER: [],
    showOnLeg: [],
    hideOnLeg: [],
  };

  // Wrapper functions that track actions AND execute grid operations
  const trackShowOnLeg = (fields: string[]) => {
    actions.showOnLeg.push(...fields);
    showOnLeg(esid, gridRef, fields);
  };

  const trackHideOnLeg = (fields: string[], forceRefresh: boolean = false) => {
    actions.hideOnLeg.push(...fields);
    hideOnLeg(esid, gridRef, fields, forceRefresh);
  };

  const trackShowOnER = (fields: string[]) => {
    actions.showOnER.push(...fields);
    showOnER(esid, gridRef, fields);
  };

  const trackHideOnER = (fields: string[]) => {
    actions.hideOnER.push(...fields);
    hideOnER(esid, gridRef, fields);
  };

  //showOnER(esid, gridRef, ["dealtFee"]); // #14571, #14573, #14575, #14577, #14579, #14581 - Updates to Outright Swaps. - NEEDS TO BE CONFIRMED.
  //hideOnER(esid, gridRef, ["cash"]); // #14571, #14573, #14575, #14577, #14579, #14581 - Updates to Outright Swaps.- NEEDS TO BE CONFIRMED.

  if (unwind) {
    // LQC-2668 - NPV-style trades never toggled book, so a row left open from
    // before Awarded only got it back on collapse and re-expand.
    if (statusFromAwarded.includes(trade.changes.status.newValue)) {
      trackShowOnLeg(["book"]);
    } else {
      trackHideOnLeg(["book"]);
    }

    if (role == "RP") {
      if (statusFromAwarded.includes(trade.changes.status.newValue)) {
        trackShowOnLeg(["dealer", "cash", "indicativeCash"]);
        if (statusAtExecutedRange.includes(trade.changes.status.newValue)) {
          trackShowOnER(["quotedSize"]);
        } else {
          trackHideOnER(["quotedSize"]);
        }
      } else {
        trackHideOnLeg(["dealer", "cash", "indicativeCash"]);
        trackShowOnER(["quotedSize"]);
      }

      if (
        statusFormExecutedToAffirmedRange.includes(
          trade.changes.status.newValue,
        )
      ) {
        trackShowOnLeg(["executionFee"]);
      } else {
        trackHideOnLeg(["executionFee"]);
      }

      if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
        trackShowOnLeg(["dealtFee"]);
        trackHideOnLeg(["cash", "executionFee"]);
        trackShowOnER(["quotedSize"])
      }
    } else if (role == "OP") {
      if (
        statusFormExecutedToAffirmedRange.includes(
          trade.changes.status.newValue,
        )
      ) {
        trackHideOnER(["indicativeCash"]);
        trackHideOnLeg(["indicativeCash"]);
        trackShowOnLeg(["dealer", "cash", "executionFee", "indicativeCash"]);
      } else {
        trackShowOnER(["indicativeCash"]);
        trackShowOnLeg(["indicativeCash"]);
        trackHideOnLeg(["dealer", "cash", "executionFee"]);
      }

      if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
        trackShowOnLeg(["dealtFee"]);
        trackHideOnLeg(["cash", "executionFee"]);
      }
    }
  }
  // Section for trades that have had their ReCoupon status changed through the trade life cycle and NO LONGER contain a recoupon block.
  if (!unwind && !containsRecoupon && !containsNPV) {
    // Can't be an NPV or it will try and hide NPV fields.
    // Hide all
    trackHideOnLeg(["coupon", "dealer", "cash", "executionFee"]);
  } // Place before the contains NPV and ReCoupon to allow NPVs a chance to add cols back as normal.

  if (!unwind && containsRecoupon) {
    if (role == "OP") {
      if (
        statusFormExecutedToAffirmedRange.includes(
          trade.changes.status.newValue,
        ) ||
        trade.changes.status.newValue == ExecutionStrategyStatus.Awarded
      ) {
        if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
          trackShowOnER(["feeAdjustedQuote"]);
          trackShowOnLeg([
            "coupon",
            "dealer",
            "feeAdjustedQuote",
            "dealtFee",
            "indicativeCash",
            "book",
          ]);
          trackHideOnLeg(["cash", "executionFee"]);
        } else if (
          trade.changes.status.newValue == ExecutionStrategyStatus.Awarded
        ) {
          // #14876, #14877, #14878, #14879, #14880, #14881 - Recoupon at awarded also requires warning message at Awarded.
          trackShowOnLeg([
            "coupon",
            "dealer",
            "cash",
            "indicativeCash",
            "book",
          ]);
          // If coming back from Executed - need to hide:
          trackHideOnER(["feeAdjustedQuote"]);
          trackHideOnLeg(["feeAdjustedQuote"]);
        } else if (
          statusAtExecutedRange.includes(trade.changes.status.newValue)
        ) {
          trackShowOnER(["feeAdjustedQuote"]);
          trackShowOnLeg([
            "coupon",
            "dealer",
            "cash",
            "executionFee",
            "indicativeCash",
            "feeAdjustedQuote",
            "book",
          ]);
        }
      } else {
        // Else used to remove if moving back a status
        trackHideOnER(["feeAdjustedQuote"]);
        const isForcingColRemoval = containsNPV == false;
        trackHideOnLeg(
          [
            "coupon",
            "dealer",
            "cash",
            "executionFee",
            "indicativeCash",
            "feeAdjustedQuote",
            "book",
          ],
          isForcingColRemoval,
        );
      }
    } else if (role == "RP") {
      if (
        statusAtExecutedAndAwardedRange.includes(trade.changes.status.newValue)
      ) {
        // #14876, #14877, #14878, #14879, #14880, #14881 - Recoupon at awarded also requires warning message at Awarded.
        trackShowOnLeg(["coupon", "unwind", "dealer", "cash", "book"]);
        if (statusAtExecutedRange.includes(trade.changes.status.newValue)) {
          // 14527 - BUG 'Fee' Now showing on NON NPV trades.
          trackShowOnER(["feeAdjustedQuote"]);
          trackShowOnLeg(["executionFee", "feeAdjustedQuote"]);
          trackShowOnER(["feeAdjustedQuote"]);
        } else {
          trackHideOnER(["feeAdjustedQuote"]);
          trackHideOnLeg(["executionFee", "feeAdjustedQuote"]);
        }
      } else {
        // Only force col removal if there is NO NPV
        const isForcingColRemoval = containsNPV == false;
        trackHideOnLeg(
          ["coupon", "unwind", "dealer", "cash", "executionFee", "book"],
          isForcingColRemoval,
        );
      }

      if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
        trackShowOnER(["feeAdjustedQuote"]);
        trackShowOnLeg([
          "coupon",
          "unwind",
          "dealer",
          "feeAdjustedQuote",
          "dealtFee",
          "book",
        ]);
        trackHideOnLeg(["cash"]);
      }
    }
  }

  if (!unwind && containsNPV) {
    if (role == "OP") {
      if (statusFromAwarded.includes(trade.changes.status.newValue)) {
        // LQC-2668 - book stays from Awarded on, matching the RP arm below.
        // Awarded-only left an open OP row without book once it hit Executed.
        trackShowOnLeg(["book"]);
        if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
          trackHideOnLeg(["executionFee", "cash"]);
          trackShowOnLeg(["feeAdjustedQuote", "dealtFee"]);
          trackShowOnER(["feeAdjustedQuote"]);
        } else if (
          statusAtExecutedRange.includes(trade.changes.status.newValue)
        ) {
          trackShowOnLeg(["executionFee", "feeAdjustedQuote"]);
        } else {
          trackHideOnLeg(["executionFee", "feeAdjustedQuote"]);
        }
      } else {
        trackHideOnLeg(["book"]);
      }
    } else if (role == "RP") {
      if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
        trackHideOnLeg(["executionFee", "cash"]);
        trackShowOnLeg(["feeAdjustedQuote", "book", "dealtFee"]);
        trackShowOnER(["feeAdjustedQuote"]);
        // 14581: OUTRIGHT - RP NPV BLOCK
        // if (strategyType == StrategyType.Outright) {
        //   showOnER(esid, gridRef, ["dealtFee"]);
        //   hideOnER(esid, gridRef, ["cash"]);
        // }
      } else if (statusFromAwarded.includes(trade.changes.status.newValue)) {
        trackShowOnLeg(["book"]);
        if (statusAtExecutedRange.includes(trade.changes.status.newValue)) {
          trackShowOnLeg(["executionFee", "fee", "feeAdjustedQuote"]);
          trackShowOnER(["feeAdjustedQuote"]);
        } else {
          // Remove when moving back.
          // If coming back from Executed - need to hide:
          trackHideOnER(["feeAdjustedQuote"]);
          trackHideOnLeg(["feeAdjustedQuote"]);
          trackHideOnLeg(["executionFee"]);
        }
      } else {
        // Remove when moving back.
        trackHideOnLeg(["book"]);
      }
    }
  }
  return actions;
}

export function columnVisabilityOutright(
  trade: any,
  gridRef: any,
  containsNPV: boolean,
  containsRecoupon: boolean,
): ColumnVisibilityActions {
  const role: "OP" | "RP" = trade.role;
  const esid = trade.id;

  // Initialize the actions object to track what columns should be shown/hidden
  const actions: ColumnVisibilityActions = {
    showOnER: [],
    hideOnER: [],
    showOnLeg: [],
    hideOnLeg: [],
  };

  // Wrapper functions that track actions AND execute grid operations
  const trackShowOnER = (fields: string[], forceRefresh: boolean = false) => {
    actions.showOnER.push(...fields);
    showOnER(esid, gridRef, fields, forceRefresh);
  };

  const trackHideOnER = (fields: string[]) => {
    actions.hideOnER.push(...fields);
    hideOnER(esid, gridRef, fields);
  };

  if (containsNPV || containsRecoupon) {
    // NPV only - Cols from the start.
    if (containsNPV || role == "OP") {
      // NOT and. ALL OP have from this stage, OR RP at status.
      if (statusFromAwarded.includes(trade.changes.status.newValue)) {
        // 14585: OUTRIGHT
        trackShowOnER(["dealer", "cash", "couponOutright", "indicativeCash"]);
      }
      trackShowOnER(["unwind"]);
    }
    // Will add feeAdjustedQuoteOutright at affirmed and Exectued
    if (
      statusFormExecutedToAffirmedRange.includes(trade.changes.status.newValue)
    ) {
      trackShowOnER(["feeAdjustedQuote"]);
    } else {
      trackHideOnER(["feeAdjustedQuote"]);
    }
  }

  // Awarded onwards.
  if (statusFromAwarded.includes(trade.changes.status.newValue)) {
    trackShowOnER(["book"]);
  } else {
    trackHideOnER(["book"]);
  }
  // From affirmed
  if (statusAtAffirmedRange.includes(trade.changes.status.newValue)) {
    if (containsNPV || containsRecoupon) {
      // 14589 - OUTRIGHT
      trackHideOnER(["cash", "executionFeeOutright"]);
      trackShowOnER(
        [
          "dealer",
          "couponOutright",
          "dealtFeeOutright", // Cash fee inc - Should this be outright???
          "feeAdjustedQuote",
        ],
        true, // Force the refresh.
      );
    }
  } else if (statusFromAwarded.includes(trade.changes.status.newValue)) {
    // Handle show at Awarded
    if (containsRecoupon && role == "RP") {
      trackShowOnER(["dealer", "cash", "couponOutright", "unwind"]);
    }
    // Handle at executed within the awarded plus range.
    if (statusAtExecutedRange.includes(trade.changes.status.newValue)) {
      // Exectued onwards
      trackShowOnER(["feeAdjustedQuote"]);
      if (containsNPV || containsRecoupon) {
        trackShowOnER(["executionFeeOutright"]); // Fee
        // trackHideOnER(["feeAdjustedQuote"]); // Hide the normal Dealt Price
      }
    } else {
      // Hide once NOT in the exectued range BUT still at awarded.
      trackHideOnER(["feeAdjustedQuote"]);
      if (containsNPV || containsRecoupon) {
        // BOTH OP 14575 and RP 14577
        trackHideOnER(["executionFeeOutright"]); // Fee
        trackHideOnER(["feeAdjustedQuote"]);
      }
    }
  } else {
    // hide if NO LONGER at awarderd or above. RP specific to ReCoupon.
    // if (containsRecoupon && role == "RP" && !containsNPV) {
    //15013 Both OR and RP have the cols removed if going back in status on Outright trades the are ONLY recoupon.
    if (containsRecoupon && !containsNPV) {
      if (role == "RP") {
        trackHideOnER([
          "dealer",
          "cash",
          "couponOutright",
          "indicativeCash",
          "unwind", // Extra field on RP is removed when moving back.
        ]);
      }
      trackHideOnER(["dealer", "cash", "couponOutright", "indicativeCash"]);
    }
  }

  return actions;
}

export function columnVisabilitySYNREPO(
  trade: any,
  gridRef: any,
): ColumnVisibilityActions {
  const esid = trade.id;
  const role = trade.role;

  // Initialize the actions object to track what columns should be shown/hidden
  const actions: ColumnVisibilityActions = {
    showOnER: [],
    hideOnER: [],
    showOnLeg: [],
    hideOnLeg: [],
  };

  // Wrapper functions that track actions AND execute grid operations
  const trackShowOnLeg = (fields: string[]) => {
    actions.showOnLeg.push(...fields);
    showOnLeg(esid, gridRef, fields);
  };

  const trackHideOnLeg = (fields: string[]) => {
    actions.hideOnLeg.push(...fields);
    hideOnLeg(esid, gridRef, fields);
  };

  const trackShowOnER = (fields: string[]) => {
    actions.showOnER.push(...fields);
    showOnER(esid, gridRef, fields);
  };

  const trackHideOnER = (fields: string[]) => {
    actions.hideOnER.push(...fields);
    hideOnER(esid, gridRef, fields);
  };

  if (role == "OP") {
    if (statusFromAwarded.includes(trade.changes.status.newValue)) {
      trackShowOnLeg(["book"]);
    } else {
      trackHideOnLeg(["book"]);
    }
    // Exectued onwards OP.
    const synRepoLegValues = ["bid", "ask", "mid", "feeAdjustedQuote", "strike"];
    if (
      statusFormExecutedToAffirmedRange.includes(trade.changes.status.newValue)
    ) {
      trackShowOnER(["feeAdjustedQuote"]);
      trackShowOnLeg(synRepoLegValues);
    } else {
      trackHideOnER(["feeAdjustedQuote"]);
      trackHideOnLeg(synRepoLegValues);
    }
  } else if (role == "RP") {
    if (statusFromAwarded.includes(trade.changes.status.newValue)) {
      trackShowOnLeg(["book", "bid", "ask", "mid"]);
    } else {
      trackHideOnLeg(["book", "bid", "ask", "mid"]);
    }
    // Exectued onwards OP.
    if (
      statusFormExecutedToAffirmedRange.includes(trade.changes.status.newValue)
    ) {
      trackShowOnER(["feeAdjustedQuote"]);
      trackShowOnLeg(["feeAdjustedQuote", "strike"]);
    } else {
      trackHideOnER(["feeAdjustedQuote"]);
      trackHideOnLeg(["feeAdjustedQuote", "strike"]);
    }
  }

  return actions;
}

//
export function columnVisabilityStructures(
  trade: any,
  gridRef: any,
): ColumnVisibilityActions {
  const esid = trade.id;

  // Initialize the actions object to track what columns should be shown/hidden
  const actions: ColumnVisibilityActions = {
    showOnER: [],
    hideOnER: [],
    showOnLeg: [],
    hideOnLeg: [],
  };

  // Wrapper functions that track actions AND execute grid operations
  const trackShowOnLeg = (fields: string[]) => {
    actions.showOnLeg.push(...fields);
    showOnLeg(esid, gridRef, fields);
  };

  const trackHideOnLeg = (fields: string[]) => {
    actions.hideOnLeg.push(...fields);
    hideOnLeg(esid, gridRef, fields);
  };

  const trackShowOnER = (fields: string[]) => {
    actions.showOnER.push(...fields);
    showOnER(esid, gridRef, fields);
  };

  const trackHideOnER = (fields: string[]) => {
    actions.hideOnER.push(...fields);
    hideOnER(esid, gridRef, fields);
  };

  // Awarded onwards.
  if (statusFromAwarded.includes(trade.changes.status.newValue)) {
    trackShowOnLeg(["book"]);
  } else {
    trackHideOnLeg(["book"]);
  }
  // Exectued onwards.
  // TODO: Does this mean no need for any NPV / Recoupon logic around this?
  if (
    statusFormExecutedToAffirmedRange.includes(trade.changes.status.newValue)
  ) {
    trackShowOnER(["feeAdjustedQuote"]);
    trackShowOnLeg(["feeAdjustedQuote"]);
  } else {
    trackHideOnER(["feeAdjustedQuote"]);
    trackHideOnLeg(["feeAdjustedQuote"]);
  }
  return actions;
}

// Order-specific column visibility
export function columnVisabilityOrders(
  trade: any,
  gridRef: any,
): ColumnVisibilityActions {
  const esid = trade.id;

  const actions: ColumnVisibilityActions = {
    showOnER: [],
    hideOnER: [],
    showOnLeg: [],
    hideOnLeg: [],
  };

  const trackShowOnLeg = (fields: string[]) => {
    actions.showOnLeg.push(...fields);
    showOnLeg(esid, gridRef, fields);
  };

  const trackHideOnLeg = (fields: string[]) => {
    actions.hideOnLeg.push(...fields);
    hideOnLeg(esid, gridRef, fields);
  };

  const trackShowOnER = (fields: string[]) => {
    actions.showOnER.push(...fields);
    showOnER(esid, gridRef, fields);
  };

  const trackHideOnER = (fields: string[]) => {
    actions.hideOnER.push(...fields);
    hideOnER(esid, gridRef, fields);
  };

  const newStatus = trade.changes.status.newValue;

  // Working status onwards: Show book column at leg level.
  // LQC-2668 - shares statusFromAwarded with the colDef build so a live status
  // change and a fresh expand agree (covers No More Fills and Change Pending).
  const showBook = statusFromAwarded.includes(newStatus);

  if (showBook) {
    trackShowOnLeg(["book"]);
  } else {
    trackHideOnLeg(["book"]);
  }

  // Filled status onwards: Show dealt price at both ER and Leg level
  // ChangePending excluded - dealt price hidden while RP is editing fills
  const filledOrBeyond =
    newStatus === ExecutionStrategyStatus.Filled ||
    newStatus === ExecutionStrategyStatus.NoMoreFills ||
    newStatus === ExecutionStrategyStatus.Affirmed;

  if (filledOrBeyond) {
    trackShowOnER(["feeAdjustedQuote"]); // Display of Dealt Price
    trackShowOnLeg(["feeAdjustedQuote"]); // Display of Dealt Price
  } else {
    trackHideOnER(["feeAdjustedQuote"]); // Display of Dealt Price
    trackHideOnLeg(["feeAdjustedQuote"]); // Display of Dealt Price
  }

  // LQC-2668 - no addFillButton toggle here. AddFillButtonCellRenderer already
  // returns null per status, and cycling the column made the cells flash green.

  // Propagate column visibility changes to fills tables (replicated columns)
  propagateColumnVisibilityToFills(
    esid,
    gridRef,
    trade.role as "OP" | "RP",
    actions,
  );

  return actions;
}

function handleShowOrHideCols(trade: any, gridRef: any) {
  if (trade.changes.status == undefined) {
    return;
  }

  // LQC-1306: cover, coverSpread and competition are populated on the trade
  // root by the backend at Executed. Their colDef-build-time visibility check
  // only runs on row expand, so already-expanded rows keep them hidden until
  // manual collapse/re-expand. Toggle visibility at runtime here.
  handleCoverColumnVisibility(trade, gridRef);

  // Handle Orders first - isolated from other trade type logic
  if (trade.tradeType === TradeType.Order) {
    columnVisabilityOrders(trade, gridRef);
    // Don't return early - Orders might also need NPV/Recoupon logic if applicable
    // return;
  }

  const strategyType = trade.strategyType;
  const structureType = trade.structureType;
  const unwind = trade.unwind;
  const containsRecoupon = trade.containsRecoupon;
  const containsNPV = trade.containsNPV;

  // // Section for trades that have had their ReCoupon status changed through the trade life cycle and NO LONGER contain a recoupon block.
  // if (!unwind && !containsRecoupon) {
  //   // Hide all
  //   hideOnLeg(trade.esid, gridRef, [
  //     "coupon",
  //     "dealer",
  //     "cash",
  //     "executionFee",
  //   ]);
  // } // Place before the contains NPV and ReCoupon to allow NPVs a chance to add cols back as normal.

  if (
    (strategyType != StrategyType.Outright && unwind == true) ||
    (strategyType != StrategyType.Outright && containsRecoupon == true) ||
    (strategyType != StrategyType.Outright && containsNPV == true)
  ) {
    columnVisabilityNPVandRecoupon(trade, gridRef); // NOT OUTRIGHT
  } else if (strategyType == StrategyType.Outright) {
    columnVisabilityOutright(trade, gridRef, containsNPV, containsRecoupon);
  } else if (strategyType == StrategyType.Structure) {
    if (
      structureType == StructureTypeCode.SYNREPO ||
      structureType == StructureTypeCode.SYNREVREP
    ) {
      columnVisabilitySYNREPO(trade, gridRef);
    } else {
      //Remaining structures
      columnVisabilityStructures(trade, gridRef);
    }
  }
}

// LQC-1306: RP-side cover, coverSpread and competition columns surface only
// when the OP has advised the value (typically at Executed). The colDef build
// flips `hide` via the selection arrays — that runs at row expand. For trades
// already expanded when the status flips to Executed we need a runtime
// visibility toggle so the user does not have to collapse + re-expand the row.
function handleCoverColumnVisibility(trade: any, gridRef: any) {
  if (trade.role !== "RP") return; // columns only exist on RP-side colDefs
  const esid = trade.id;
  // hasCoverValue treats "" (a removed value) as absent, matching the cell
  // display — `!= null` alone leaves the column visible on an empty string.
  controlVisibleColInERLevel(esid, gridRef, ["cover"], hasCoverValue(trade.cover));
  controlVisibleColInERLevel(
    esid,
    gridRef,
    ["coverSpread"],
    hasCoverValue(trade.coverSpread),
  );
  controlVisibleColInERLevel(
    esid,
    gridRef,
    ["competition"],
    hasCoverValue(trade.competition),
  );
}

export async function handleShowOrHideColsFromNodeExpand(
  params: any,
  gridRef: React.RefObject<AgGridReact<any>>,
  fullTradeData: any,
) {
  if (params.node && params.node.detailNode) {
    const tradeData = tradeDataStore.getState().tradeData;
    const currentTrade = tradeData.find(
      (trade) => trade.esid == fullTradeData.esid,
    );

    await delayViaPromise(10); // Need to let the API load.

    if (currentTrade) {
      let containsRecoupon = false;
      let containsNPV = false;
      if (
        currentTrade.role == "OP" &&
        currentTrade.executionRequestCounterParties
      ) {
        if (
          currentTrade.executionRequestCounterParties.length > 0 &&
          currentTrade.executionRequestCounterParties[0].legs
        ) {
          currentTrade.executionRequestCounterParties[0].legs.forEach(
            (leg: any) => {
              if (leg.unwind == "Recoupon") {
                containsRecoupon = true;
              }
              if (leg.unwind == "NPV") {
                containsNPV = true;
              }
            },
          );
        }
      } else if (currentTrade.role == "RP" && currentTrade.legs) {
        if (currentTrade.legs.length > 0) {
          currentTrade.legs.forEach((leg: any) => {
            if (leg.unwind == "Recoupon") {
              containsRecoupon = true;
            } else if (leg.unwind == "NPV") {
              containsNPV = true;
            }
          });
        }
      }

      const trade = {
        changes: { status: { newValue: currentTrade.status } },
        id: currentTrade.esid,
        role: currentTrade.role,
        strategyType: currentTrade.strategyType,
        structureType: currentTrade.structureType,
        unwind: currentTrade.unwind,
        containsRecoupon: containsRecoupon,
        containsNPV: containsNPV,
        cover: (currentTrade as any).cover,
        coverSpread: (currentTrade as any).coverSpread,
        competition: (currentTrade as any).competition,
      };
      handleShowOrHideCols(trade, gridRef);
    }
  }
}

function isAlreadyVisibleColInERLevel(
  esid: string,
  gridRef: any,
  fields: string[],
): boolean {
  let allColsAlreadyVisible = true;

  if (gridRef && gridRef.current) {
    const node = gridRef?.current.api.getRowNode(esid);
    if (node) {
      const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
        `detail_${esid}`,
      );
      if (detailGridInfo && detailGridInfo.api) {
        // fields.forEach((field) => {
        const currentColState = detailGridInfo.api.getColumnState();
        fields.every((field) => {
          const currentColDef = currentColState.find(
            (col: any) => col.colId == field,
          );
          if (currentColDef && currentColDef.hide == true) {
            return (allColsAlreadyVisible = false);
          }
        });
      }
    }
  }
  return allColsAlreadyVisible;
}

function controlVisibleColInERLevel(
  esid: string,
  gridRef: any,
  fields: string[],
  setVisible: boolean, // True shows the col.
) {
  if (gridRef && gridRef.current) {
    const node = gridRef?.current.api.getRowNode(esid);
    if (node) {
      const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
        `detail_${esid}`,
      );
      if (detailGridInfo && detailGridInfo.api) {
        detailGridInfo.api.setColumnsVisible(fields, setVisible);
      }
    }
  }
}

// Push fresh master-row data into the ER-level and leg-level detail grids for RP trades.
// applyTransaction({ update }) was tried first but fails with "could not find row id=undefined"
// because detail grid rows have no ID. node.setData() bypasses that requirement.
function syncERDetailRowData(esid: string, gridRef: any) {
  if (!gridRef?.current) return;
  const masterNode = gridRef.current.api.getRowNode(esid);
  if (!masterNode?.data) return;
  const detailGridInfo = gridRef.current.api.getDetailGridInfo(`detail_${esid}`);
  if (!detailGridInfo?.api) return;

  detailGridInfo.api.forEachNode((erNode: any) => {
    erNode.setData(masterNode.data);

    // Also sync leg-level rows nested under this ER row so statusER is fresh.
    const nestedGridInfo = detailGridInfo.api.getDetailGridInfo(`detail_${erNode.id}`);
    if (nestedGridInfo?.api && masterNode.data.legs) {
      nestedGridInfo.api.forEachNode((legNode: any) => {
        const freshLeg = masterNode.data.legs.find(
          (l: any) => l.blockId === legNode.data?.blockId,
        );
        if (freshLeg) legNode.setData(freshLeg);
      });
    }
  });
}

// export function applyTransactionsToDetailTable(esid: string, gridRef: any) {
//   if (gridRef && gridRef.current) {
//     let node = gridRef?.current.api.getRowNode(esid);
//     if (node) {
//       const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
//         `detail_${esid}`,
//       );
//       if (detailGridInfo && detailGridInfo.api) {

//         detailGridInfo.api.applyTransaction({ update: [node.data] });

//         // const NestedDetailGridInfo = detailGridInfo.api.getDetailGridInfo(
//         //   `detail_${esid}`,
//         // );

//         // if (NestedDetailGridInfo && NestedDetailGridInfo.api) {
//         //   NestedDetailGridInfo.api.redrawRows(); //({ update: [node.data] });
//         // }
//         //AG Grid: could not find row id=undefined, data item was not found for this id
//       }
//     }
//   }
// }

export function refreshColInERLevel(
  esid: string,
  gridRef: any,
  fields: string[],
  suppressFlash = false,
  refreshClassRules = false,
) {
  if (gridRef && gridRef.current) {
    const node = gridRef?.current.api.getRowNode(esid);
    if (node) {
      const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
        `detail_${esid}`,
      );
      if (detailGridInfo && detailGridInfo.api) {
        detailGridInfo.api.refreshCells({
          columns: fields,
          force: true,
          suppressFlash: suppressFlash,
          refreshClassRules: refreshClassRules,
        });
      }
    }
  }
}

function isAlreadyVisibleColInLegLevel(
  esid: string,
  gridRef: any,
  fields: string[],
): boolean {
  let allColsAlreadyVisible = true;

  if (gridRef && gridRef.current) {
    if (!gridRef?.current) return false;

    const topLevelApi = gridRef.current.api;

    // Get mid-level grid information
    const midLevelGridInfo = topLevelApi.getDetailGridInfo(`detail_${esid}`);
    if (!midLevelGridInfo?.api) return false;

    // Flash all rows in the mid-level grid
    const midLevelApi = midLevelGridInfo.api;
    const midLevelNodes: any[] = [];
    midLevelApi.forEachNode((node: any) => {
      midLevelNodes.push(node);
    });

    // Iterate through each mid-level node to check nested grids
    midLevelNodes.forEach((midNode) => {
      const nestedGridId = `detail_${midNode.id}`;
      const nestedDetailGridInfo = midLevelApi.getDetailGridInfo(nestedGridId);

      if (nestedDetailGridInfo?.api) {
        const nestedApi = nestedDetailGridInfo.api;

        // Retrieve the column state for the nested grid
        const currentColState = nestedApi.getColumnState();

        // Check the visibility of each column in the fields array
        fields.forEach((field) => {
          const currentColDef = currentColState.find(
            (col: any) => col.colId === field,
          );

          // If the column is hidden, set allColsAlreadyVisible to false
          if (currentColDef && currentColDef.hide === true) {
            allColsAlreadyVisible = false;
          }
        });
      }
    });

    return allColsAlreadyVisible;
  }

  // if (gridRef && gridRef.current) {
  //   let node = gridRef?.current.api.getRowNode(esid);
  //   if (node) {
  //     const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
  //       `detail_${esid}`,
  //     );
  //     if (detailGridInfo && detailGridInfo.api) {
  //       // fields.forEach((field) => {
  //       let currentColState = detailGridInfo.api.getColumnState();
  //       fields.every((field) => {
  //         let currentColDef = currentColState.find(
  //           (col: any) => col.colId == field,
  //         );
  //         if (currentColDef.hide == true) {
  //           return (allColsAlreadyVisible = false);
  //         }
  //       });
  //     }
  //   }
  // }
  return allColsAlreadyVisible;
}

function controlVisibleColInLegLevel(
  esid: string,
  gridRef: any,
  fields: string[],
  setVisible: boolean, // True shows the col.
) {
  if (gridRef && gridRef.current) {
    if (!gridRef?.current) return;

    const topLevelApi = gridRef.current.api;
    const midLevelGridInfo = topLevelApi.getDetailGridInfo(`detail_${esid}`);
    if (!midLevelGridInfo?.api) return;

    // Flash all rows in the mid-level grid
    const midLevelApi = midLevelGridInfo.api;
    const midLevelNodes: any[] = [];
    midLevelApi.forEachNode((node: any) => {
      midLevelNodes.push(node);
    });

    // Try to find all nested detail grids inside midLevelApi
    midLevelNodes.forEach((midNode) => {
      const nestedGridId = `detail_${midNode.id}`;
      const nestedDetailGridInfo = midLevelApi.getDetailGridInfo(nestedGridId);

      if (nestedDetailGridInfo?.api) {
        const nestedApi = nestedDetailGridInfo.api;
        const nestedNodes: any[] = [];

        nestedApi.forEachNode((node: any) => {
          nestedNodes.push(node);
        });

        nestedApi.setColumnsVisible(fields, setVisible);
      }
    });
  }
}

function flashCellsInERLevel(esid: string, gridRef: any, field: string) {
  if (gridRef && gridRef.current) {
    const node = gridRef?.current.api.getRowNode(esid);
    if (node) {
      const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
        `detail_${esid}`,
      );
      if (detailGridInfo && detailGridInfo.api) {
        detailGridInfo.api.flashCells({
          // rowNodes: [],
          columns: [field],
        });
      }
    }
  }
}

export function flashCellInLegLevel(esid: string, gridRef: any, field: string) {
  if (gridRef && gridRef.current) {
    const node = gridRef?.current.api.getRowNode(esid);
    if (node) {
      const detailGridInfo = gridRef.current!.api.getDetailGridInfo(
        `detail_${esid}`,
      );
      if (detailGridInfo && detailGridInfo.api) {
        const innerNode = detailGridInfo.api.getRowNode(esid);
        if (innerNode) {
          detailGridInfo.api.flashCells({
            rowNodes: [innerNode],
            columns: [field],
          });
        }

        const NestedDetailGridInfo = detailGridInfo.api.getDetailGridInfo(
          `detail_${esid}`,
        );

        if (NestedDetailGridInfo && NestedDetailGridInfo.api) {
          NestedDetailGridInfo.api.refreshCells({
            force: true,
            columns: [field],
          });
        }
      }
    }
  }
}

export function refreshCellInLegLevel(
  esid: string,
  gridRef: any,
  fields: string[],
  suppressFlash = false,
  refreshClassRules = false,
) {
  if (!gridRef?.current) return;

  const topLevelApi = gridRef.current.api;
  const midLevelGridInfo = topLevelApi.getDetailGridInfo(`detail_${esid}`);
  if (!midLevelGridInfo?.api) return;

  const midLevelApi = midLevelGridInfo.api;
  const midLevelNodes: any[] = [];
  midLevelApi.forEachNode((node: any) => {
    midLevelNodes.push(node);
  });

  midLevelNodes.forEach((midNode) => {
    const nestedGridId = `detail_${midNode.id}`;
    const nestedDetailGridInfo = midLevelApi.getDetailGridInfo(nestedGridId);

    if (nestedDetailGridInfo?.api) {
      const nestedApi = nestedDetailGridInfo.api;
      const nestedNodes: any[] = [];

      nestedApi.forEachNode((node: any) => {
        nestedNodes.push(node);
      });

      nestedApi.refreshCells({
        force: true,
        rowNodes: nestedNodes,
        columns: fields,
        suppressFlash,
        refreshClassRules,
      });
    }
  });
}

function refreshCell(
  gridRef: React.RefObject<AgGridReact<any>>,
  esid: string,
  field: string,
) {
  if (gridRef && gridRef.current && gridRef.current.api) {
    const rowNode = gridRef.current.api.getRowNode(esid);
    if (!rowNode) {
      console.warn(`Row with ID ${esid} not found.`);
      return; // Exit function if the row doesn't exist
    }
    gridRef.current.api.refreshCells({
      rowNodes: [rowNode], // Find row by ID
      columns: [field], // Specify the column to refresh
      force: true, // Ensure re-render
    });
  }
}

// ---- -----

export function returnRowIDAtFirstDetailTable(
  params: any,
  nestedParams: any,
  tradeDetails: ITradeDetails,
) {
  let dataToPass = "";
  if (tradeDetails.isViewingLegs) {
    dataToPass = nestedParams.data.id;
  } else if (tradeDetails.isViewingAllocaitons) {
    dataToPass = nestedParams.data.id;
  } else if (tradeDetails.isCancelledOPWithNoCP) {
    dataToPass = nestedParams.data.id;
  } else if (tradeDetails.isOp) {
    dataToPass = nestedParams.data.executionRequestId;
  } else {
    dataToPass = `${params.data.esid}`;
  }
  return dataToPass;
}

export function returnRowDataAtDetailTable(
  params: any,
  tradeDetails: ITradeDetails,
  getAccessTokenSilently: Function,
) {
  let dataToPass = [];

  if (tradeDetails.isViewingLegs || tradeDetails.isCancelledOPWithNoCP) {
    dataToPass =
      tradeBlockDataStore
        .getState()
        .tradeBlockData.find((trade) => trade.esid == params.data.esid)
        ?.blocks || [];
    // if the zustand store for the blocks does not have the correct data, return empty array.
    if (tradeDetails.isCancelledOPWithNoCP && dataToPass?.length == 0) {
      getBlocksData({
        esid: params.data.esid,
        getAccessTokenSilently: getAccessTokenSilently,
      });
    }
  } else if (tradeDetails.isViewingAllocaitons) {
    dataToPass = params.data.allocations;
  } else if (
    tradeDetails.isOp &&
    !tradeDetails.isTradeStatusNew &&
    !tradeDetails.isEditingPanel &&
    !tradeDetails.isNotSentAndClosed
  ) {
    dataToPass = params.data.executionRequestCounterParties;
  } else {
    dataToPass = [params.data];
  }
  return params.successCallback(dataToPass);
}

/**
 * Flattens blocks and their fills into a single array for AG Grid tree data.
 * Block rows get dataPath ["block-{blockId}"], fill rows get ["block-{blockId}", "fill-{fillId}"].
 * This eliminates the need for a nested fills table (NESTED TABLE 3) while
 * preserving expand/collapse via AG Grid's tree data feature.
 *
 * @param legs - Array of leg/block data from the ER level
 * @param esid - The execution strategy ID for looking up fresh fills from the store
 * @param role - "RP" or "OP" to determine where to find fills in the store
 */
export function flattenBlocksAndFills(
  legs: ExecutionRequestQuoteLeg[],
  esid: string,
  role: string,
): IFlattenedLegRow[] {
  const flattenedRows: IFlattenedLegRow[] = [];

  // Get fresh fills from the store (RP may have locally added fills)
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);

  legs.forEach((leg) => {
    // Add block row
    flattenedRows.push({
      ...leg,
      rowType: "block",
      dataPath: [`block-${leg.blockId}`],
    });

    // Find fills for this block from the store
    let fills: IOrderFill[] = [];

    if (!trade) {
      // Trade not in store (e.g. historical search) - use fills from params data
      fills = leg.fills ?? [];
    } else if (role === "RP" && trade?.legs) {
      const storeLeg = trade.legs.find((l) => l.blockId === leg.blockId);
      fills = storeLeg?.fills ?? [];
    } else if (role === "OP" && trade?.executionRequestCounterParties) {
      for (const cp of trade.executionRequestCounterParties) {
        if (cp.legs) {
          const storeLeg = cp.legs.find((l) => l.blockId === leg.blockId);
          if (storeLeg?.fills && storeLeg.fills.length > 0) {
            fills = storeLeg.fills;
            break;
          }
        }
      }
    }

    // Sort fills by extFillId for stable ordering (preserves order across backend fillId changes)
    const sortedFills = [...fills].sort((a, b) => {
      if (a.extFillId != null && b.extFillId != null) {
        return Number(a.extFillId) - Number(b.extFillId);
      }
      return 0; // Maintain current order when extFillId not yet assigned
    });

    // Add fill rows as children of the block
    sortedFills.forEach((fill) => {
      // Use extFillId for stable row identification if available, fallback to fillId
      const stableFillId = fill.extFillId ?? fill.fillId;
      flattenedRows.push({
        ...leg, // Inherit block data for column alignment
        rowType: "fill",
        parentBlockId: leg.blockId,
        fillData: { ...fill, blockId: leg.blockId },
        fillId: fill.fillId, // Exposed at top level for cell renderer compatibility
        fillRate: fill.fillRate, // Exposed for params.value fallback (historical trades)
        fillSize: fill.fillSize, // Exposed for params.value fallback (historical trades)
        // filledAt: fill.filledAt, // Exposed for params.value fallback (historical trades)
        dataPath: [
          `block-${leg.blockId}`,
          `fill-${leg.blockId}-${stableFillId}`,
        ],
        // Clear block-level summary fields so they don't display on fill rows
        fills: undefined,
      });
    });
  });

  return flattenedRows;
}

// 3rd Nested table -
export function returnRowDataAtNestedLegTable(
  nestedParams: any,
  tradeDetails: ITradeDetails,
  fullTradeData?: ExecutionStrategy,
) {
  let dataToPass: any[] = [];
  if (tradeDetails.isOp) {
    dataToPass = nestedParams.data?.legs ?? [];
  } else {
    dataToPass = nestedParams.data.legs ?? [];
  }

  // For Order trades, flatten blocks and fills into the same array
  // so they render in a single grid with tree data expand/collapse
  if (
    fullTradeData?.type === TradeType.Order &&
    fullTradeData.esid &&
    fullTradeData.role &&
    !tradeDetails.isEditingPanel
  ) {
    const flattenedData = flattenBlocksAndFills(
      dataToPass,
      fullTradeData.esid,
      fullTradeData.role,
    );
    return nestedParams.successCallback(flattenedData);
  }

  return nestedParams.successCallback(dataToPass);
}

export function returnColDefsForDetailTableAtActionSummary(
  colDefOptionsObj: IColDefsObject,
  tradeDetails: ITradeDetails,
) {
  if (tradeDetails.isViewingLegs) {
    return returnColDefsForLegs(colDefOptionsObj);
  }
  if (tradeDetails.isViewingAllocaitons) {
    return returnColDefsForAllocations(colDefOptionsObj);
  }
  if (tradeDetails.isEditingPanel) {
    return returnColDefsForEditingPanel(colDefOptionsObj);
  }
  if (tradeDetails.isTradeStatusNew) {
    return returnColDefsForTradeAtNew(colDefOptionsObj);
  }
  if (tradeDetails.isOp) {
    if (tradeDetails.isCancelledOPWithNoCP) {
      return returnColDefsForLegs(colDefOptionsObj); // Switch back to legs in this scenario
    }
    return returnExecutionRequestColDefsOP(colDefOptionsObj);
  }
  return returnExecutionRequestColDefsRP(colDefOptionsObj);
}

// Handle the multiFilter for Actioned at.
export function actionSummaryFilterChangeHandler(
  event: any,
  previousFilterModel: any,
) {
  const filterModel = event.api.getFilterModel();
  let currentFilterModelToReturn = filterModel;
  //SET the previousFilterModel if it's empty (first load)
  if (previousFilterModel.current.toString() == "{}") {
    previousFilterModel.current = filterModel;
  }
  if (
    filterModel.ActionSummaryActionedAtCell &&
    filterModel.ActionSummaryActionedAtCell.filterModels
  ) {
    const currentFilterModels =
      filterModel.ActionSummaryActionedAtCell.filterModels;

    if (
      previousFilterModel.current &&
      previousFilterModel.current.ActionSummaryActionedAtCell
    ) {
      // Comparison here: Filter out the old.
      const currentModelLength = currentFilterModels.filter(
        (model: any) => model != null,
      ).length;

      if (currentModelLength > 1) {
        let existingFilterType = "";
        if (
          previousFilterModel.current.ActionSummaryActionedAtCell
            .filterModels[0] != null
        ) {
          existingFilterType =
            previousFilterModel.current.ActionSummaryActionedAtCell
              .filterModels[0].filterType;
        } else if (
          previousFilterModel.current.ActionSummaryActionedAtCell
            .filterModels[1] != null
        ) {
          existingFilterType =
            previousFilterModel.current.ActionSummaryActionedAtCell
              .filterModels[1].filterType;
        }

        const newFilterModel = JSON.parse(JSON.stringify(filterModel));

        newFilterModel.ActionSummaryActionedAtCell.filterModels =
          newFilterModel.ActionSummaryActionedAtCell.filterModels.map(
            (model: any) => {
              if (model.filterType != existingFilterType) {
                return model;
              }
              return null;
            },
          );
        event.api.setFilterModel(newFilterModel);
        currentFilterModelToReturn = newFilterModel;
      }
    }
    previousFilterModel.current = currentFilterModelToReturn;
    return currentFilterModelToReturn;
  }
}

// ---- Order Fills Nested Table Helper Functions ----

/**
 * Propagates column visibility changes from leg level to fills tables.
 * Since fills tables now have replicated columns that mirror leg-level columns,
 * we need to sync visibility changes to maintain alignment.
 */
export async function propagateColumnVisibilityToFills(
  esid: string,
  gridRef: React.RefObject<AgGridReact<any>>,
  role: "OP" | "RP",
  actions: ColumnVisibilityActions,
): Promise<void> {
  // Wait for column visibility changes to apply at leg level
  await delayViaPromise(50);

  if (!gridRef?.current?.api) return;

  const topLevelApi = gridRef.current.api;

  // Get the mid-level (ER) grid
  const midLevelGridInfo = topLevelApi.getDetailGridInfo(`detail_${esid}`);
  if (!midLevelGridInfo?.api) return;

  const midLevelApi = midLevelGridInfo.api;

  // Determine which replicated columns to show/hide based on actions
  const colsToShowOnFills = actions.showOnLeg.filter((col) =>
    fillsReplicatedColIds.includes(col as any),
  );
  const colsToHideOnFills = actions.hideOnLeg.filter((col) =>
    fillsReplicatedColIds.includes(col as any),
  );

  // Helper to update fills table visibility
  const updateFillsGridVisibility = (fillsGridApi: any) => {
    if (!fillsGridApi) return;

    colsToShowOnFills.forEach((colId) => {
      fillsGridApi.setColumnVisible(colId, true);
    });
    colsToHideOnFills.forEach((colId) => {
      fillsGridApi.setColumnVisible(colId, false);
    });
  };

  // For OP: mid-level is counterparties, need to go to leg level
  // For RP: mid-level IS the leg level
  if (role === "OP") {
    // Iterate through counterparty nodes to find leg grids
    midLevelApi.forEachNode((cpNode: any) => {
      const legGridId = `detail_${cpNode.id}`;
      const legGridInfo = midLevelApi.getDetailGridInfo(legGridId);

      if (legGridInfo?.api) {
        const legApi = legGridInfo.api;

        // Update visibility in any expanded fills tables
        legApi.forEachNode((legNode: any) => {
          const fillsGridId = `detail_${legNode.id}`;
          const fillsGridInfo = legApi.getDetailGridInfo(fillsGridId);

          if (fillsGridInfo?.api) {
            updateFillsGridVisibility(fillsGridInfo.api);
          }
        });
      }
    });
  } else {
    // RP: mid-level is already the leg level
    midLevelApi.forEachNode((legNode: any) => {
      const fillsGridId = `detail_${legNode.id}`;
      const fillsGridInfo = midLevelApi.getDetailGridInfo(fillsGridId);

      if (fillsGridInfo?.api) {
        updateFillsGridVisibility(fillsGridInfo.api);
      }
    });
  }
}

/**
 * Handles OP Order fills arriving while at Working status (no status change).
 * Detects which blocks received new fills and expands those block rows.
 */
export function handleOPOrderFillsArriving(
  trade: IFindChangedDataOutput,
  gridRef: React.RefObject<AgGridReact<any>>,
): void {
  if (!trade.changes.executionRequestCounterParties) return;

  const oldCPs = trade.changes.executionRequestCounterParties.oldValue;
  const newCPs = trade.changes.executionRequestCounterParties.newValue;

  if (!oldCPs || !newCPs) return;

  // Find blockIds that received new fills
  const blockIdsWithNewFills: number[] = [];

  newCPs.forEach((newCP: any) => {
    if (!newCP.legs) return;

    const oldCP = oldCPs.find(
      (cp: any) => cp.executionRequestId === newCP.executionRequestId,
    );

    newCP.legs.forEach((newLeg: any) => {
      const oldLeg = oldCP?.legs?.find(
        (leg: any) => leg.blockId === newLeg.blockId,
      );

      const oldFillCount = oldLeg?.fills?.length || 0;
      const newFillCount = newLeg?.fills?.length || 0;

      // If fills were added to this block
      if (newFillCount > oldFillCount && newFillCount > 0) {
        blockIdsWithNewFills.push(newLeg.blockId);
      }
    });
  });

  // If any blocks received new fills, redraw and expand those rows
  if (blockIdsWithNewFills.length > 0) {
    redrawRowsViaESID(trade.id, gridRef);
    expandBlockRowsForFills(trade.id, gridRef, blockIdsWithNewFills);
  }
}

/**
 * RP equivalent of handleOPOrderFillsArriving.
 * Detects when the backend has assigned new fillIds (after processing a fill update)
 * and redraws the detail grid so cell renderers get fresh params.data.
 *
 * Only fires when fillIds change (backend update), NOT on frontend edits
 * (which only change fillType/fillRate/fillSize, not fillId). This avoids flicker.
 */
export function handleRPOrderFillsUpdated(
  trade: IFindChangedDataOutput,
  gridRef: React.RefObject<AgGridReact<any>>,
): void {
  if (!trade.changes.legs) return;

  const oldLegs = trade.changes.legs.oldValue;
  const newLegs = trade.changes.legs.newValue;

  if (!oldLegs || !newLegs) return;

  // Compare fillIds between old and new legs
  // Frontend edits don't change fillIds — only backend updates do
  const fillIdsChanged = newLegs.some((newLeg: any) => {
    const oldLeg = oldLegs.find((l: any) => l.blockId === newLeg.blockId);

    const oldFillIds = new Set((oldLeg?.fills ?? []).map((f: any) => f.fillId));
    const newFillIds = new Set((newLeg?.fills ?? []).map((f: any) => f.fillId));

    if (oldFillIds.size !== newFillIds.size) return true;
    for (const id of newFillIds) {
      if (!oldFillIds.has(id)) return true;
    }
    return false;
  });

  if (fillIdsChanged) {
    redrawRowsViaESID(trade.id, gridRef);
  }
}

/**
 * Expands specific block rows in the leg-level grid to show the fills table.
 */
export function expandBlockRowsForFills(
  esid: string,
  gridRef: React.RefObject<AgGridReact<any>>,
  blockIds: number[],
): void {
  if (!gridRef?.current?.api) return;

  const topLevelApi = gridRef.current.api;

  // Get the mid-level (ER) grid
  const midLevelGridInfo = topLevelApi.getDetailGridInfo(`detail_${esid}`);
  if (!midLevelGridInfo?.api) return;

  const midLevelApi = midLevelGridInfo.api;

  // Iterate through mid-level nodes (counterparties for OP)
  midLevelApi.forEachNode((midNode: any) => {
    const nestedGridId = `detail_${midNode.id}`;
    const legLevelGridInfo = midLevelApi.getDetailGridInfo(nestedGridId);

    if (!legLevelGridInfo?.api) return;

    const legLevelApi = legLevelGridInfo.api;

    // Find and expand the block rows that received fills
    legLevelApi.forEachNode((legNode: any) => {
      if (blockIds.includes(legNode.data?.blockId)) {
        legNode.setExpanded(true);
      }
    });
  });
}

// Determines if the Order fills nested table should be shown at the leg level
// Reads status directly from Zustand store to ensure live data
export function masterDetailAtLegLevelForOrders(
  tradeType: string | undefined,
  esid: string | undefined,
  isEditingPanel?: boolean,
): boolean {
  // Don't show fills table when viewing edit panel
  if (isEditingPanel) return false;
  if (tradeType !== TradeType.Order) return false;
  if (!esid) return false;

  // Get live status from the Zustand store
  const tradeData = tradeDataStore.getState().tradeData;
  const trade = tradeData.find((t) => t.esid === esid);
  const status = trade?.status;

  if (
    status === ExecutionStrategyStatus.Sent ||
    status === ExecutionStrategyStatus.Received ||
    status === ExecutionStrategyStatus.ReSent ||
    status === ExecutionStrategyStatus.Acknowledged
  ) {
    return false;
  }

  return true;
}

/**
 * Determines if a specific leg should show the fills dropdown.
 * - For RP: Always returns true (RP initializes fills locally)
 * - For OP: Only returns true if the leg has fills in the store
 *
 * This is used as the isRowMaster callback for the fills nested table.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID
 * @param role - The trade role ("RP" or "OP")
 */
export function shouldShowFillsForLeg(
  esid: string | undefined,
  blockId: number | undefined,
  role: string | undefined,
): boolean {
  // RP always shows fills dropdown (fills are initialized locally)
  // Check role BEFORE checking esid/blockId to avoid breaking RP behavior
  if (role === "RP") return true;

  // For OP, need valid esid and blockId to check for fills
  if (!esid || blockId === undefined) return false;

  // For OP, check if the leg has fills in the store
  if (role === "OP") {
    const tradeData = tradeDataStore.getState().tradeData;
    const trade = tradeData.find((t) => t.esid === esid);

    if (trade?.executionRequestCounterParties) {
      for (const cp of trade.executionRequestCounterParties) {
        if (cp.legs) {
          const leg = cp.legs.find((l) => l.blockId === blockId);
          if (leg?.fills && leg.fills.length > 0) {
            return true;
          }
        }
      }
    }
    // OP has no fills for this leg - don't show dropdown
    return false;
  }

  return false;
}

/**
 * Ensures all legs in a trade have at least one fill entry.
 * If any leg has empty fills, adds default fills to ALL empty legs in a single store update.
 * This prevents multiple store updates when a trade has multiple legs with empty fills.
 *
 * Note: This function only initializes fills when the trade status is "Working".
 * The flow is: RP receives trade at "Received" status (no fill editing),
 * RP actions "Working" status, then fills are initialized.
 *
 * @param fullTradeData - The full trade data object containing esid and role
 */
export function ensureTradeFillsInitialized(fullTradeData: any): void {
  if (!fullTradeData?.esid) return;

  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === fullTradeData.esid);

  if (tradeIndex === -1) return;

  const trade = tradeData[tradeIndex];
  const [updatedTrade] = initializeFillsForWorkingOrders([trade]);

  if (updatedTrade !== trade) {
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? updatedTrade : t,
    );
    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  // Handle OP trades - fills are in executionRequestCounterParties > legs
  // TODO: OP should be showing the fills they were sent.
  // if (trade.role === "OP" && trade.executionRequestCounterParties) {
  //   let needsUpdate = false;

  //   const newCPs = trade.executionRequestCounterParties.map((cp) => {
  //     if (!cp.legs) return cp;

  //     const newLegs = cp.legs.map((leg) => {
  //       if (!leg.fills || leg.fills.length === 0) {
  //         needsUpdate = true;
  //         return {
  //           ...leg,
  //           fills: [
  //             {
  //               fillId: 1,
  //               price: undefined,
  //               quantity: leg.notionalLeg1,
  //               status: "New" as const,
  //               blockId: leg.blockId,
  //             },
  //           ],
  //         };
  //       }
  //       return leg;
  //     });

  //     return { ...cp, legs: newLegs };
  //   });

  //   if (needsUpdate) {
  //     const newTrade = { ...trade, executionRequestCounterParties: newCPs };
  //     const newTradeData = tradeData.map((t, i) =>
  //       i === tradeIndex ? newTrade : t,
  //     );
  //     tradeDataStore.getState().updateTradeData(newTradeData);
  //   }
  // }
}

/**
 * Adds a new fill entry to a specific block's fills array.
 * The new fill will have the next available fillId.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID to add the fill to
 */
export function addNewFillToBlock(esid: string, blockId: number): void {
  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === esid);

  if (tradeIndex === -1) return;

  const trade = tradeData[tradeIndex];

  // Handle RP trades - fills are in trade.legs
  if (trade.role === "RP" && trade.legs) {
    const newLegs = trade.legs.map((leg) => {
      if (leg.blockId !== blockId) return leg;

      const currentFills = leg.fills || [];

      // Calculate next fillId (find max and add 1)
      const maxFillId = currentFills.reduce((max, fill) => {
        const fillIdNum =
          typeof fill.fillId === "number"
            ? fill.fillId
            : parseInt(fill.fillId as string, 10) || 0;
        return Math.max(max, fillIdNum);
      }, 0);

      // Calculate maxExtFillId independently for stable ordering
      const maxExtFillId = currentFills.reduce((max, fill) => {
        return fill.extFillId != null
          ? Math.max(max, Number(fill.extFillId))
          : max;
      }, currentFills.length); // Default to fill count when no extFillIds exist

      // Calculate remaining quantity (notionalLeg1 - total filled)
      const blockNotional = leg.notionalLeg1 || 0;
      const totalFilled = currentFills.reduce(
        (sum, fill) => sum + (fill.fillSize || 0),
        0,
      );
      const remainingQuantity = Math.max(blockNotional - totalFilled, 0);

      const nextId = maxFillId + 1;
      const newFill: IOrderFill = {
        fillId: nextId,
        extFillId: maxExtFillId + 1,
        fillRate: undefined,
        fillSize: remainingQuantity,
        fillType: "New" as const,
        blockId: leg.blockId,
      };

      return {
        ...leg,
        fills: [...currentFills, newFill],
      };
    });

    const newTrade = { ...trade, legs: newLegs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );
    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  // Handle OP trades - fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    const newCPs = trade.executionRequestCounterParties.map((cp) => {
      if (!cp.legs) return cp;

      const newLegs = cp.legs.map((leg) => {
        if (leg.blockId !== blockId) return leg;

        const currentFills = leg.fills || [];

        // Calculate next fillId
        const maxFillId = currentFills.reduce((max, fill) => {
          const fillIdNum =
            typeof fill.fillId === "number"
              ? fill.fillId
              : parseInt(fill.fillId as string, 10) || 0;
          return Math.max(max, fillIdNum);
        }, 0);

        // Calculate maxExtFillId independently for stable ordering
        const maxExtFillId = currentFills.reduce((max, fill) => {
          return fill.extFillId != null
            ? Math.max(max, Number(fill.extFillId))
            : max;
        }, currentFills.length);

        // Calculate remaining quantity (notionalLeg1 - total filled)
        const blockNotional = leg.notionalLeg1 || 0;
        const totalFilled = currentFills.reduce(
          (sum, fill) => sum + (fill.fillSize || 0),
          0,
        );
        const remainingQuantity = Math.max(blockNotional - totalFilled, 0);

        const nextId = maxFillId + 1;
        const newFill: IOrderFill = {
          fillId: nextId,
          extFillId: maxExtFillId + 1,
          fillRate: undefined,
          fillSize: remainingQuantity,
          fillType: "New" as const,
          blockId: leg.blockId,
        };

        return {
          ...leg,
          fills: [...currentFills, newFill],
        };
      });

      return { ...cp, legs: newLegs };
    });

    const newTrade = { ...trade, executionRequestCounterParties: newCPs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );
    tradeDataStore.getState().updateTradeData(newTradeData);
  }
}

/**
 * Removes, cancels, or clears a fill entry from a specific block's fills array.
 *
 * Behavior depends on fill state:
 * - "New (Update Pending)" fills: Remove completely (unless last fill, then clear values)
 * - Other fills (existing from backend): Set to "Cancel (Update Pending)" and preserve historic values
 *
 * A block must ALWAYS have at least 1 fill.
 *
 * @param esid - The execution strategy ID
 * @param blockId - The block/leg ID to remove the fill from
 * @param fillId - The fill ID to remove
 * @returns "removed" | "cancelled" | "cleared" - The action taken on the fill
 */
export function removeOrClearFill(
  esid: string,
  blockId: number,
  fillId: number,
): "removed" | "cancelled" | "cleared" {
  const tradeData = tradeDataStore.getState().tradeData;
  const tradeIndex = tradeData.findIndex((t) => t.esid === esid);

  if (tradeIndex === -1) return "removed";

  const trade = tradeData[tradeIndex];
  let actionTaken: "removed" | "cancelled" | "cleared" = "removed";

  /**
   * Processes fills for a given leg based on the fill's state.
   */
  const processFills = (currentFills: IOrderFill[]) => {
    const targetFill = currentFills.find((f) => f.fillId === fillId);
    if (!targetFill) return { fills: currentFills, action: "removed" as const };

    const isNewFill = targetFill.fillType === "New";
    const fillCount = currentFills.length;

    // Case 1: New fill AND not the last fill -> Remove completely
    if (isNewFill && fillCount > 1) {
      return {
        fills: currentFills.filter((fill) => fill.fillId !== fillId),
        action: "removed" as const,
      };
    }

    // Case 2: New fill AND is the last fill -> Clear values (block must have at least 1 fill)
    if (isNewFill && fillCount === 1) {
      return {
        fills: [],
        action: "removed" as const,
        //   currentFills.map((fill) => ({
        //   ...fill,
        //   fillRate: undefined,
        //   fillSize: undefined,
        // })),
        // action: "cleared" as const,
      };
    }

    // Case 3: Existing fill (not new) -> Set to cancelled, preserve historic values
    return {
      fills: currentFills.map((fill) => {
        if (fill.fillId !== fillId) return fill;
        return {
          ...fill,
          // Only set historic values if not already preserved (e.g., from prior Update)
          historicFillRate:
            fill.historicFillRate !== undefined
              ? fill.historicFillRate
              : fill.fillRate,
          historicFillSize:
            fill.historicFillSize !== undefined
              ? fill.historicFillSize
              : fill.fillSize,
          fillRate: undefined,
          fillSize: undefined,
          fillType: "Cancel" as const,
        };
      }),
      action: "cancelled" as const,
    };
  };

  // Handle RP trades - fills are in trade.legs
  if (trade.role === "RP" && trade.legs) {
    const newLegs = trade.legs.map((leg) => {
      if (leg.blockId !== blockId) return leg;

      const currentFills = leg.fills ?? [];
      const result = processFills(currentFills);
      actionTaken = result.action;

      return { ...leg, fills: result.fills };
    });

    const newTrade = { ...trade, legs: newLegs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );

    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  // Handle OP trades - fills are in executionRequestCounterParties > legs
  if (trade.role === "OP" && trade.executionRequestCounterParties) {
    const newCPs = trade.executionRequestCounterParties.map((cp) => {
      if (!cp.legs) return cp;

      const newLegs = cp.legs.map((leg) => {
        if (leg.blockId !== blockId) return leg;

        const currentFills = leg.fills ?? [];
        const result = processFills(currentFills);
        actionTaken = result.action;

        return { ...leg, fills: result.fills };
      });

      return { ...cp, legs: newLegs };
    });

    const newTrade = { ...trade, executionRequestCounterParties: newCPs };
    const newTradeData = tradeData.map((t, i) =>
      i === tradeIndex ? newTrade : t,
    );

    tradeDataStore.getState().updateTradeData(newTradeData);
  }

  return actionTaken;
}

/**
 * Returns row data for the order fills nested table.
 * First ensures fills are initialized in the store, then returns the fills from the store.
 *
 * @param nestedParams - AG Grid nested params containing the leg data
 * @param fullTradeData - The full trade data object for accessing esid and role
 */
export function returnRowDataAtOrderFillsTable(
  nestedParams: any,
  fullTradeData?: any,
) {
  // Ensure fills are initialized for all legs in a single batch update
  // if (fullTradeData) {
  //   ensureTradeFillsInitialized(fullTradeData);
  // }

  // Now get fills from the store (which will have the initialized data)
  const esid = fullTradeData?.esid;
  const blockId = nestedParams.data?.blockId;

  if (esid && blockId !== undefined) {
    const tradeData = tradeDataStore.getState().tradeData;
    const trade = tradeData.find((t) => t.esid === esid);

    if (trade) {
      // For RP trades
      if (trade.role === "RP" && trade.legs) {
        const leg = trade.legs.find((l) => l.blockId === blockId);
        if (leg?.fills && leg.fills.length > 0) {
          // Add blockId to each fill for the cell renderers
          const fillsWithBlockId = leg.fills.map((fill) => ({
            ...fill,
            blockId: leg.blockId,
          }));
          return nestedParams.successCallback(fillsWithBlockId);
        }
      }

      // For OP trades
      if (trade.role === "OP" && trade.executionRequestCounterParties) {
        for (const cp of trade.executionRequestCounterParties) {
          if (cp.legs) {
            const leg = cp.legs.find((l) => l.blockId === blockId);
            if (leg?.fills && leg.fills.length > 0) {
              const fillsWithBlockId = leg.fills.map((fill) => ({
                ...fill,
                blockId: leg.blockId,
              }));
              return nestedParams.successCallback(fillsWithBlockId);
            }
          }
        }
      }
    }
  }

  // Fallback: return empty array to prevent "Loading..." state
  // This should only be reached for OP trades with no fills
  return nestedParams.successCallback([]);
}

// LQC-1844 — flash a row when its request-info status changes (an RP request
// arrives, or one is reconciled after a settings Update). We deliberately do NOT
// route this through eisdsToFlash: the grid subscribes to that array and would
// re-render, and a request-info change carries no tradeData update to give AG Grid
// a setRowData transaction that preserves expansion — so re-rendering would
// collapse the open master/detail row (the LQC-1844 bug). Subscribing to the store
// imperatively (outside React render) lets us flash via the grid API with no
// re-render. requestInfo[esid] is replaced by reference on each upsert, so an
// identity diff tells us exactly which esid(s) changed.
// Returns the store's unsubscribe fn so the caller's useEffect can clean up.
export function subscribeToRequestInfoFlash(
  gridRef: RefObject<AgGridReact>,
): () => void {
  return requestInformationStore.subscribe((state, prev) => {
    if (state.requestInfo === prev.requestInfo) return;
    if (!gridRef.current?.api) return;

    const nodesToFlash: IRowNode[] = [];
    Object.keys(state.requestInfo).forEach((esid) => {
      if (state.requestInfo[esid] === prev.requestInfo[esid]) return;
      const node = gridRef.current?.api.getRowNode(esid);
      if (node) nodesToFlash.push(node);
    });
    if (nodesToFlash.length === 0) return;

    gridRef.current.api.flashCells({
      rowNodes: nodesToFlash,
      fadeDuration: 1000,
      flashDuration:
        agGridFlashNotifications.getState().tradeBlotterFlashDuration,
    });
  });
}
